import { Form, Input, Select } from "antd";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDepartmentGradesList } from "@/modules/department-grades/hooks/useDepartmentGrades";
import { DepartmentTreeSelect } from "@/shared/components/DepartmentTreeSelect";
import { FormModal } from "@/shared/components/FormModal";
import type { Department, DepartmentFormValues } from "../types";

interface DepartmentFormModalProps {
	open: boolean;
	editing: Department | null;
	departments: Department[];
	/** Yaratishda tanlangan tugun bo'lsa — shu ota-ona sifatida oldindan tanlanadi. */
	defaultParentId?: string | null;
	/**
	 * Yaratishda "Ota bo'lim" `defaultParentId` ga QULFLANADI — maydon ko'rinadi
	 * (operator qaysi ota ostida yaratayotganini ko'radi), lekin o'zgartirib ham,
	 * tozalab ham bo'lmaydi. Bo'lim profilining "Bo'linmalar" tabi uchun: u yerda
	 * yangi bo'linma HAR DOIM shu bo'lim ostida yaratiladi, tozalash esa
	 * placeholder'dagi «Ildiz bo'lim (tanlanmasa)» va'dasiga zid bo'lardi.
	 * Qulf daraja taklifini ham izchil saqlaydi — u faqat `defaultParentId` dan
	 * hisoblanadi.
	 */
	parentLocked?: boolean;
	isSubmitting: boolean;
	onCancel: () => void;
	onSubmit: (values: DepartmentFormValues) => void;
}

/**
 * Ota-bo'lim darajasidan KEYINGI eng yaqin (eng kichik KATTA) katalog qatori.
 * Ota yo'q (ildiz) bo'lsa — katalogning eng past darajasi. Mos qator yo'q
 * bo'lsa `undefined`: operator o'zi tanlaydi (maydon majburiy).
 */
function suggestNextGradeId(
	parentGradeLevel: number | null,
	grades: { id: string; level: number }[]
): string | undefined {
	const candidates = grades
		.filter((g) => parentGradeLevel === null || g.level > parentGradeLevel)
		.sort((a, b) => a.level - b.level);

	return candidates[0]?.id;
}

export function DepartmentFormModal({
	open,
	editing,
	departments,
	defaultParentId,
	parentLocked = false,
	isSubmitting,
	onCancel,
	onSubmit,
}: DepartmentFormModalProps) {
	const { t } = useTranslation("departments");
	const isEditing = editing !== null;
	// ⚠️ `Form.useForm()` shu yerda — `FormModal`ga uzatiladi, chunki taklif
	// effekti (pastda) `form.isFieldTouched`/`setFieldValue`ni to'g'ridan-
	// to'g'ri chaqirishi kerak.
	const [form] = Form.useForm<DepartmentFormValues>();
	// ⚠️ `enabled: open` — modal sahifada shartsiz mount qilinadi; katalog
	// (`department-grades:read`) faqat forma OCHILGANDA so'raladi.
	const { data: grades = [] } = useDepartmentGradesList({ enabled: open });

	// Yangi bo'lim yaratishda o'z-o'ziga yoki avlodiga ota-ona qilib berish
	// mumkin emas — ko'chirish xuddi shu cheklovni "Ko'chirish" modalida
	// alohida ko'rsatadi, bu yerda esa yaratishda bunday muammo yo'q
	// (yangi bo'limning hali avlodi bo'lmaydi).
	const availableDepartments = departments.filter(
		(department) => !isEditing || department.id !== editing.id
	);

	// ⚠️ Taklif faqat MODAL OCHILGANDA (`defaultParentId` bo'yicha) hisoblanadi —
	// `TreeSelect`da ota-bo'lim qo'lda almashtirilganda qayta hisoblanmaydi
	// (3-reja Task 4 doirasi); operator Daraja maydonini o'zi o'zgartiradi.
	const parentGradeLevel = defaultParentId
		? (departments.find((d) => d.id === defaultParentId)?.gradeLevel ?? null)
		: null;
	const suggestedGradeId = isEditing ? undefined : suggestNextGradeId(parentGradeLevel, grades);

	/**
	 * ⚠️⚠️ **`suggestedGradeId` bu yerda DEPS'GA KIRMAYDI — ataylab.**
	 * `useMemo` MAJBURIY: `FormModal` `initialValues` havolasi o'zgarganda
	 * formani `resetFields()` qiladi. Katalog (`grades`) sovuq keshda
	 * MODAL OCHILGANDAN KEYIN kelishi mumkin — `suggestedGradeId` deps'da
	 * bo'lganda bu `initialValues` havolasini yangilab, operator
	 * ALLAQACHON yozgan "Nomi"ni `resetFields()` bilan o'chirib yuborardi.
	 * Taklif endi PASTDAGI effekt orqali, faqat `gradeId` maydoniga
	 * to'g'ridan-to'g'ri (`form.setFieldValue`) qo'yiladi.
	 */
	const initialValues = useMemo(
		() =>
			editing
				? {
						name: editing.name,
						parentId: editing.parentId ?? undefined,
						gradeId: editing.gradeId,
						code: editing.code ?? undefined,
					}
				: { parentId: defaultParentId ?? undefined },
		[editing, defaultParentId]
	);

	/**
	 * ⚠️⚠️ **Kechikib kelgan taklifni qo'yish — `resetFields()` SIZ.**
	 * `suggestedGradeId` o'zgarganda (masalan katalog endi yuklandi)
	 * `gradeId` maydoniga FAQAT operator hali TEGMAGAN bo'lsa
	 * (`isFieldTouched`) yoziladi — aks holda operatorning ongli tanlovi
	 * (yoki keyinroq bo'sh qoldirgani) ustidan yozib yuborilardi.
	 * Tahrirlashda ISHLATILMAYDI: `editing.gradeId` allaqachon barqaror
	 * `initialValues`da, katalog vaqti unga bog'liq emas.
	 *
	 * ⚠️ `open` DEPS'DA: `FormModal` HAR OCHILISHDA `resetFields()`
	 * chaqiradi (`isFieldTouched` ni ham tozalaydi) — usiz modal
	 * yopib-qayta ochilganda (masalan bekor qilib qayta bosilganda),
	 * `suggestedGradeId` O'ZGARMAGAN bo'lsa, bu effekt qayta ishlamay
	 * qolib, maydon bo'sh qolardi.
	 */
	// biome-ignore lint/correctness/useExhaustiveDependencies: `open` tanasida ishlatilmaydi, lekin `FormModal`ning har ochilishdagi `resetFields()`idan KEYIN qayta ishlashi uchun MAJBURIY
	useEffect(() => {
		if (!isEditing && suggestedGradeId !== undefined && !form.isFieldTouched("gradeId")) {
			form.setFieldValue("gradeId", suggestedGradeId);
		}
	}, [isEditing, suggestedGradeId, form, open]);

	/**
	 * Bo'sh/bo'shliqli kod `null` bo'lib ketadi: backend `code` ni `nullish()`
	 * qabul qiladi va `""` ni bazaga BO'SH SATR sifatida yozardi; tahrirlashda
	 * esa `undefined` "o'zgarmasin" degani — kodni tozalab bo'lmasdi.
	 * Kesish yozish paytida EMAS (`normalize`), yuborishda — aks holda
	 * "AND 04" dagi bo'shliqni yozib bo'lmasdi.
	 */
	const handleSubmit = (values: DepartmentFormValues) => {
		onSubmit({ ...values, code: values.code?.trim() || null });
	};

	return (
		<FormModal<DepartmentFormValues>
			open={open}
			form={form}
			title={isEditing ? t("form.editTitle", { name: editing.name }) : t("form.createTitle")}
			initialValues={initialValues}
			isSubmitting={isSubmitting}
			onCancel={onCancel}
			onSubmit={handleSubmit}
		>
			<Form.Item
				name="name"
				label={t("form.nameLabel")}
				rules={[{ required: true, message: t("form.nameRequired") }]}
			>
				<Input placeholder={t("form.namePlaceholder")} />
			</Form.Item>

			{!isEditing && (
				<Form.Item name="parentId" label={t("form.parentLabel")}>
					<DepartmentTreeSelect
						departments={availableDepartments}
						placeholder={t("form.parentPlaceholder")}
						disabled={parentLocked}
						allowClear={!parentLocked}
					/>
				</Form.Item>
			)}

			<Form.Item
				name="gradeId"
				label={t("form.gradeLabel")}
				rules={[{ required: true, message: t("form.gradeRequired") }]}
				extra={t("form.gradeHint")}
			>
				<Select
					options={grades.map((g) => ({ value: g.id, label: `${g.level} · ${g.name}` }))}
					placeholder={t("form.gradePlaceholder")}
				/>
			</Form.Item>

			<Form.Item name="code" label={t("form.codeLabel")}>
				<Input placeholder={t("form.codePlaceholder")} />
			</Form.Item>
		</FormModal>
	);
}
